import {
  MaskFillColor,
  WhiteLayerMaskMode,
  type PreviewBaseProps,
  type WhiteLayerMask,
} from '@athena-lib/LivePreview/types';

type DrawOptions = {
  fillStyle?: MaskFillColor;
  globalCompositeOperation: GlobalCompositeOperation;
  filterMode: WhiteLayerMaskMode;
  imgWidth: number;
  imgHeight: number;
};

type MaskImage = {
  img: HTMLImageElement;
  color: string;
};

export default async function generateMasks(props: PreviewBaseProps) {
  const imgSrc = props.sticker.whiteLayerMasks[0].maskImgUrl;
  const img = await loadImage(imgSrc);

  const whiteLayerMaskCanvas = await generateMask(
    {
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
      fillStyle:
        props.maskBackground === WhiteLayerMaskMode.None
          ? MaskFillColor.Color
          : MaskFillColor.Effect,
      globalCompositeOperation:
        props.maskBackground === WhiteLayerMaskMode.None ? 'destination-out' : 'source-over',
      filterMode: WhiteLayerMaskMode.None,
    },
    props.maskBackground,
    props.sticker.whiteLayerMasks,
  );

  const opacityMaskCanvas = await generateMask(
    {
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
      fillStyle:
        props.maskBackground === WhiteLayerMaskMode.Full
          ? MaskFillColor.Effect
          : MaskFillColor.Color,
      globalCompositeOperation:
        props.maskBackground === WhiteLayerMaskMode.Full ? 'source-over' : 'destination-out',
      filterMode: WhiteLayerMaskMode.Full,
    },
    props.maskBackground,
    props.sticker.whiteLayerMasks,
  );

  const b64WhiteLayerMask = whiteLayerMaskCanvas.toDataURL('image/png');
  const b64OpacityMask = opacityMaskCanvas.toDataURL('image/png');

  return {
    b64WhiteLayerMask,
    b64OpacityMask,
  };
}

async function generateMask(
  drawOptions: DrawOptions,
  backgroundMode: WhiteLayerMaskMode,
  whiteLayerMasks: WhiteLayerMask[],
  setBackground = true,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    console.error('failed to get context');
    return canvas;
  }
  canvas.width = drawOptions.imgWidth;
  canvas.height = drawOptions.imgHeight;

  const loadedImages = await loadImagesForMasks(
    whiteLayerMasks,
    backgroundMode,
    drawOptions.filterMode,
  );

  drawToContext(context, canvas, loadedImages, drawOptions);

  return setBackground ? setMaskBackground(canvas) : canvas;
}

function drawToContext(
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  loadedImages: MaskImage[],
  drawOptions: DrawOptions,
) {
  if (drawOptions.fillStyle) {
    context.fillStyle = drawOptions.fillStyle;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  context.globalCompositeOperation = drawOptions.globalCompositeOperation;

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  loadedImages.forEach((image) => {
    context.drawImage(image.img, 0, 0, canvas.width, canvas.height);
  });
}

async function loadImagesForMasks(
  whiteLayerMasks: WhiteLayerMask[],
  backgroundMode: WhiteLayerMaskMode,
  filterMode: WhiteLayerMaskMode,
) {
  return await Promise.all(
    whiteLayerMasks
      .filter(
        (mask) =>
          mask.maskImgUrl !== '' &&
          ((backgroundMode === filterMode && mask.mode !== filterMode) ||
            (backgroundMode !== filterMode && mask.mode === filterMode)),
      )
      .map(async (mask) => {
        return {
          img: await loadImage(mask.maskImgUrl),
          color: mask.colorRange,
        };
      }),
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (event) => {
      console.error('Failed to load image', event);
      reject(new Error('Failed to load image'));
    };
    img.src = src;
  });
}

function setMaskBackground(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const bgCanvas = document.createElement('canvas');
  const bgContext = bgCanvas.getContext('2d');
  if (!bgContext) {
    console.error('failed to get context');
    return bgCanvas;
  }
  bgCanvas.width = canvas.width;
  bgCanvas.height = canvas.height;
  bgContext.fillStyle = MaskFillColor.Effect;
  bgContext.fillRect(0, 0, canvas.width, canvas.height);
  bgContext.drawImage(canvas, 0, 0);

  return bgCanvas;
}
export async function generateLivePreviewMask(
  props: PreviewBaseProps,
): Promise<Record<string, string>> {
  const whiteLayerMasks = props.sticker.whiteLayerMasks;
  const backgroundMode = props.maskBackground;

  const imgSrc = whiteLayerMasks[0].maskImgUrl;
  const img = await loadImage(imgSrc);

  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const whiteLayerMaskCanvas = await generateMask(
    {
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
      fillStyle: backgroundMode === WhiteLayerMaskMode.None ? MaskFillColor.Color : undefined,
      globalCompositeOperation:
        backgroundMode === WhiteLayerMaskMode.None ? 'destination-out' : 'source-over',
      filterMode: WhiteLayerMaskMode.None,
    },
    backgroundMode,
    whiteLayerMasks,
    false,
  );

  const mixLayerCanvas = await generateMask(
    {
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
      fillStyle: backgroundMode === WhiteLayerMaskMode.Mix ? MaskFillColor.Mix : undefined,
      globalCompositeOperation:
        backgroundMode === WhiteLayerMaskMode.Mix ? 'destination-out' : 'source-over',
      filterMode: WhiteLayerMaskMode.Mix,
    },
    backgroundMode,
    whiteLayerMasks,
    false,
  );

  let combinedCanvas = document.createElement('canvas');
  combinedCanvas.width = width;
  combinedCanvas.height = height;
  const combinedContext = combinedCanvas.getContext('2d');
  if (!combinedContext) {
    console.error('failed to get context');
    return {
      combinedMask: combinedCanvas.toDataURL('image/png'),
      mixMask: mixLayerCanvas.toDataURL('image/png'),
      whitelayerMask: whiteLayerMaskCanvas.toDataURL('image/png'),
    };
  }
  combinedCanvas = setMaskBackground(whiteLayerMaskCanvas);

  return {
    combinedMask: combinedCanvas.toDataURL('image/png'),
    mixMask: setMaskColor(mixLayerCanvas, MaskFillColor.Color).toDataURL('image/png'),
  };
}
function setMaskColor(canvas: HTMLCanvasElement, color: string): HTMLCanvasElement {
  const colorCanvas = document.createElement('canvas');
  const colorContext = colorCanvas.getContext('2d');
  if (!colorContext) {
    console.error('failed to get context');
    return colorCanvas;
  }
  colorCanvas.width = canvas.width;
  colorCanvas.height = canvas.height;
  colorContext.fillStyle = color;
  colorContext.fillRect(0, 0, canvas.width, canvas.height);
  colorContext.globalCompositeOperation = 'destination-in';
  colorContext.drawImage(canvas, 0, 0);

  return colorCanvas;
}
